<template>
  <form
    v-show="data.ticketId !== null || openChat"
    ref="formChatMessage"
    class="messages-bottom"
    @submit.prevent="sendMessage()"
  >
    <div
      v-if="editingMessage"
      class="edit-banner"
    >
      <div class="edit-banner__info">
        <svg
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
          class="edit-banner__icon"
        >
          <path
            d="M6.414,16,16.556,5.858,15.142,4.444,5,14.586V16Zm.829,2H3V13.757L14.435,2.322a1,1,0,0,1,1.414,0l2.829,2.829a1,1,0,0,1,0,1.414ZM3,20H21v2H3Z"
          />
        </svg>
        <div class="edit-banner__text">
          <span class="edit-banner__title">Editando mensagem</span>
          <span class="edit-banner__preview">{{ editingPreview }}</span>
        </div>
      </div>
      <button
        type="button"
        class="borderless edit-banner__close"
        aria-label="Cancelar edição"
        :disabled="sendingMessage"
        @click="cancelEdit"
      >
        <svg
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            d="M18.3 5.71 12 12.01l-6.3-6.3-1.41 1.41 6.3 6.3-6.3 6.3 1.41 1.41 6.3-6.3 6.3 6.3 1.41-1.41-6.3-6.3 6.3-6.3z"
          />
        </svg>
      </button>
    </div>

    <div
      v-show="chatOwner"
      class="send-message"
    >
      <button
        v-show="!editingMessage"
        type="button"
        class="borderless btn-attachment"
        :class="data.file.content ? 'attached-file' : ''"
        @click="openFolderDirectory"
      >
        <lila-svgIcon
          :src="require(`@assets/icons/attachment.svg`)"
          :prevent="false"
        />

        <input
          ref="fileInputRef"
          type="file"
          class="file-input"
          @change="handleFileUpload"
        >
      </button>

      <lila-input
        v-model="data.message"
        :placeholder="editingMessage ? 'Edite a mensagem' : 'Digite uma mensagem'"
        :validation="data.message"
      />

      <button
        type="submit"
        class="borderless button btn-send-message"
        :class="sendingMessage ? 'is-loading' : ''"
      >
        <lila-svgIcon
          v-show="!sendingMessage"
          :prevent="false"
          :src="require(`@assets/icons/send_message.svg`)"
        />
      </button>
    </div>
  </form>
</template>

<script setup>
import { ref, computed } from "vue";
import { useVuelidate } from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import LilaInput from "@components/shared/fields/Input.vue";
import LilaSvgIcon from "@components/shared/images/SvgIcon.vue";
import { Utilities } from "../../plugins/Utilities.js";
import axios from "../../js/axios";
import { Session } from "../../plugins/Session";
import { userSessionName } from "../../js/axios";
import { eventBus } from "../../eventBus.js";
import getValueIgnoreCase from "@js/getValueIgnoreCase.js";
import DOMPurify from "dompurify";

const p = defineProps({
  data: {
    message: {
      type: String,
      default: ""
    }
  },
  openChat: Boolean,
  chatOwner: Boolean,
  loading: Boolean,
  alert: {
    message: null,
    type: null,
    show: false
  },
  scrollChatToBottom: {
    type: Function,
    required: true
  },
  setChatAttributes: {
    type: Function,
    required: true
  },
  openTransferToProfessionalModal: {
    type: Function,
    required: true
  },
  // Mensagem em edição (vinda do Chat.vue ao clicar no lápis); null = envio normal.
  editingMessage: {
    type: Object,
    default: null
  }
});

const emit = defineEmits(["cancel-edit", "message-edited"]);

const sendingMessage = ref(false);
const fileInputRef = ref();

// Prévia curta do texto original, mostrada no banner de edição.
const editingPreview = computed(() => {
  if (!p.editingMessage) return "";
  const text =
    p.editingMessage.Message ??
    getValueIgnoreCase(p.editingMessage, "message") ??
    "";
  return text.length > 60 ? `${text.slice(0, 60)}…` : text;
});

const rules = computed(() => {
  return {
    data: {
      message: {
        required: required
      }
    }
  };
});

const v = useVuelidate(rules, p.data.message);

function openFolderDirectory() {
  fileInputRef.value.click();
}

async function handleFileUpload(event) {
  p.alert.show = false;

  const file = event.target.files[0];

  if (verifyFileExtension(file)) {
    p.data.file.content = file;
    p.data.file.title = file.name;
  } else {
    p.data.file.content = null;
    p.data.file.title = "";

    Utilities.controlAlert(
      null,
      p.alert,
      "warning",
      "É permitido apenas upload de arquivos no formato jpeg, jpg, png e pdf."
    );
  }
}

function verifyFileExtension(file) {
  return ["image/jpeg", "image/png", "application/pdf"].includes(file.type);
}

function resetFileInput() {
  p.data.file.title = "";
  p.data.file.content = "";
  if (fileInputRef.value) {
    fileInputRef.value.value = "";
  }
}

async function sendMessage() {
  p.alert.show = false;

  v.value.$touch();

  // Em modo de edição, o envio vira atualização (PUT) da mensagem.
  if (p.editingMessage) {
    await saveEdit();
    return;
  }

  if (!p.data.message && !p.data.file.content) return;

  sendingMessage.value = true;

  let formData = new FormData();
  let messageEndpoint = "";

  if (p.data.ticketId == null) {
    messageEndpoint = `api/chat/withmessage/patient/${p.data.patientId}`;
    formData.append("Priority", "Normal");
  } else {
    messageEndpoint = `api/chatmessage/chat/${p.data.ticketId}`;
  }

  if (p.data.message) {
    const sanitizedMessage = DOMPurify.sanitize(p.data.message.trim()); // ✅ Adicionado
    formData.append("Message", sanitizedMessage);
  }

  if (p.data.file.content)
    formData.append("AttachedDocument", p.data.file.content);

  p.loading = true;

  try {
    const res = await axios({
      method: "POST",
      url: messageEndpoint,
      data: formData,
      headers: {
        Authorization: `bearer ${Session.get(userSessionName).token.Value}`,
        "Content-Type": "multipart/form-data"
      }
    });

    if (p.data.ticketId == null) {
      p.setChatAttributes(res.data);
      eventBus.emit("openTicket", res.data.Id);
    } else {
      p.data.messageList.push(res.data);
    }

    p.data.message = "";
    resetFileInput();

    v.value.$reset();

    p.scrollChatToBottom();
  } catch (error) {
    if (error && error.response && error.response.status) {
      console.error(error);
      Utilities.verifyToken(error.response.status);
      Utilities.controlAlert(
        error.response.data,
        p.alert,
        "danger",
        "Ocorreu um erro, tente novamente mais tarde!"
      );
    }
  }

  sendingMessage.value = false;
}

function cancelEdit() {
  p.data.message = "";
  v.value.$reset();
  emit("cancel-edit");
}

async function saveEdit() {
  const text = (p.data.message || "").trim();
  // Espelha a validação do backend: não envia mensagem vazia.
  if (!text) return;

  sendingMessage.value = true;

  const id =
    p.editingMessage.Id ?? getValueIgnoreCase(p.editingMessage, "id");
  const sanitizedMessage = DOMPurify.sanitize(text);

  try {
    const res = await axios({
      method: "PUT",
      url: `api/ChatMessage/${id}`,
      data: { message: sanitizedMessage },
      headers: {
        Authorization: `bearer ${Session.get(userSessionName).token.Value}`,
        "Content-Type": "application/json"
      }
    });

    emit("message-edited", res.data);
    p.data.message = "";
    v.value.$reset();
  } catch (error) {
    handleEditError(error);
  } finally {
    sendingMessage.value = false;
  }
}

function handleEditError(error) {
  const status = error?.response?.status;
  Utilities.verifyToken(status);

  const serverMessage =
    error?.response?.data?.message || error?.response?.data?.Message || null;

  let message;
  switch (status) {
  case 403:
    message = "Você só pode editar suas próprias mensagens.";
    break;
  case 404:
    message = serverMessage || "Mensagem não encontrada.";
    break;
  case 400:
    message =
      serverMessage ||
      "Essa mensagem já foi respondida e não pode mais ser editada.";
    break;
  default:
    message = serverMessage || "Ocorreu um erro, tente novamente mais tarde!";
  }

  Utilities.controlAlert(null, p.alert, "danger", message);
}
</script>

<style>
.messages-bottom .edit-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 12px 10px 0;
  padding: 8px 12px;
  border-radius: 8px;
  border-left: 4px solid #9a397d;
  background-color: #f3e9f0;
}

.messages-bottom .edit-banner__info {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.messages-bottom .edit-banner__icon {
  width: 18px;
  height: 18px;
  color: #9a397d;
  flex-shrink: 0;
}

.messages-bottom .edit-banner__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.messages-bottom .edit-banner__title {
  font-size: 13px;
  font-weight: 600;
  color: #9a397d;
  line-height: 16px;
}

.messages-bottom .edit-banner__preview {
  font-size: 13px;
  color: #7a7a7a;
  line-height: 16px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.messages-bottom .edit-banner__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #9a397d;
  cursor: pointer;
  flex-shrink: 0;
  transition: background-color 0.15s ease;
}

.messages-bottom .edit-banner__close:hover:not(:disabled) {
  background-color: rgba(154, 57, 125, 0.12);
}

.messages-bottom .edit-banner__close:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.messages-bottom .edit-banner__close svg {
  width: 16px;
  height: 16px;
}
</style>
